import { useState, useEffect, useRef } from "react";
import InputText from "./InputText";
import { useSelector } from "react-redux";
import axios from "axios";
import { config } from "../../../utils/config";
import { getCookie, setCookie } from "../../../utils/cookieHandler";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import {
  calculateDateDiffWithGrace,
  disabledIfFieldChecker,
  findDataInFields,
  isFieldEnabledChecker,
} from "../../../utils/helperFuctions";

export default function InputTextTwoLabelsWithCalc({
  label,
  label2,
  label2Param,
  name,
  type,
  id,
  placeholder,
  defaultValue,
  err,
  required,
  handleChange,
  mainAction,
  handleActionClick,
  disabled,
  lazyload,
  lazyloadParam,
  body,
  lazyloadFieldChange,
  subname,
  affectedBy,
  decimalCount,
  minValue,
  maxValue,
  disabledIfField,
  disabledIfField2,
  getFromValue,
  editId,
  initialValueUrl,
  initialValueParam,
  getInitialPriceOnFieldChange,
  alwaysEnabledIf,
  quantityField,
}) {
  const currentFields = useSelector((state) => state.showFields);
  const [secondLabel, setSecondLabel] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [valueLazyloaded, setValueLazyloaded] = useState({
    previousValue: null,
    changed: false,
  });
  const [value, setValue] = useState();
  const lastRentalPricePerDay = findDataInFields(
    currentFields,
    quantityField != null ? quantityField : "rental_price_per_day",
    true
  );
  const getDaysValue = () => {
    const grace_period = findDataInFields(currentFields, "grace_period", true);
    let gp = grace_period;

    if (gp == null || gp === "") {
      const graceperiod = findDataInFields(
        currentFields,
        "branch.grace_period",
        true
      );
      gp = graceperiod;
    }
    const date1 = findDataInFields(currentFields, "pickup_date", true);
    const date2 = findDataInFields(currentFields, "return_date", true);
    return calculateDateDiffWithGrace(date1, date2, gp);
  };
  const numberOfDays = getDaysValue(); // Hardcoded for now
  const [quantity, setQuantity] = useState("");

  // console.log(value);

  const getInitialValue = async (url, param) => {
    setIsLoading(true);
    try {
      if (url) {
        const response = await axios.get(
          `${config.apiUrl}/api/${url}?id=${editId}&fieldName=${name}&`,
          {
            headers: {
              Authorization: `Bearer ${getCookie("_AT")}`,
            },
          }
        );

        // Handle authorization token if needed
        if (response.headers?.authorization) {
          if (`Bearer ${getCookie("_AT")}` !== response.headers.authorization) {
            setCookie("_AT", response.headers.authorization.split(" ")[1]);
          }
        }

        // Check if response has the required data
        if (
          response.data &&
          !Array.isArray(response.data) &&
          response.data[param] !== null &&
          response.data[param] !== undefined &&
          typeof response.data[param] !== "object" &&
          !Array.isArray(response.data[param])
        ) {
          setIsLoading(false);
          return response.data[param];
        }
        setIsLoading(false);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
    setIsLoading(false);

    return null; // Return null or any default value in case of an error
  };
  const initialFieldValueRef = useRef(
    getInitialPriceOnFieldChange != null
      ? findDataInFields(currentFields, getInitialPriceOnFieldChange.field)
      : undefined
  );

  const fieldChangeValue =
    getInitialPriceOnFieldChange != null
      ? findDataInFields(currentFields, getInitialPriceOnFieldChange.field)
      : undefined;

  useEffect(() => {
    const fetchData = async () => {
      if (
        getInitialPriceOnFieldChange != null &&
        fieldChangeValue === getInitialPriceOnFieldChange.value &&
        initialFieldValueRef.current !== fieldChangeValue // Only if changed from initial
      ) {
        const value = await getLazyload(lazyload, lazyloadParam);
        setValue(value);
      }
      // Update the ref if needed
      initialFieldValueRef.current = fieldChangeValue;
    };

    fetchData();
  }, [fieldChangeValue]);

  useEffect(() => {
    const fetchInitialValue = async () => {
      const initialValue = await getInitialValue(
        initialValueUrl,
        initialValueParam
      );

      setValue(initialValue);
      handleChange(
        {
          target: {
            name: subname ? subname : name,
            value: initialValue,
          },
        },
        false
      );
    };
    // Work around since additional charge and discount amount are not dynamically updated from form

    if (initialValueUrl != null && editId !== 0) {
      fetchInitialValue();
    }
  }, [initialValueUrl, initialValueParam, editId]);

  const inputChange = (e) => {
    const val = e.target.value;
    setValue(val);

    // Update quantity based on value (reverse calculation)
    if (!isNaN(val) && Number(numberOfDays) !== 0) {
      const calcQty = Number(val) / numberOfDays;
      setQuantity(calcQty);
      handleChange({
        target: {
          name: quantityField != null ? quantityField : "rental_price_per_day",
          value: calcQty,
        },
      });
    }

    handleChange(e);
  };

  function findData(arrayOfObjects, searchString) {
    // Split the searchString to get the object property and the key to search
    const [objectName, key] = searchString.split(".");

    // Find the object with the specified name in the array
    const foundObject = arrayOfObjects.find((obj) => obj.name === objectName);

    // If the object is found and it has a 'data' property which is an array
    if (foundObject && foundObject.data && Array.isArray(foundObject.data)) {
      // Find the object within the 'data' array with the specified id
      const foundDataObject = foundObject.data.find(
        (dataObj) => dataObj.id === foundObject.defaultValue
      );

      // If the object is found, return the value of the specified key
      if (foundDataObject && foundDataObject[key]) {
        return foundDataObject[key];
      }
    } else if (
      foundObject.defaultValue !== null &&
      foundObject.defaultValue !== undefined
    ) {
      return typeof foundObject.defaultValue === "object"
        ? Array.isArray(foundObject.defaultValue)
          ? foundObject.defaultValue
          : foundObject.defaultValue[key]
        : foundObject.defaultValue;
    }

    // Return null if not found
    return null;
  }

  const getLazyload = async (url, param) => {
    try {
      setIsLoading(true); // Start loading ✅

      if (url) {
        let tosend = {};
        if (body) {
          for (const [key, value] of Object.entries(body)) {
            tosend[key] = findData(currentFields, value);
          }
        }
        const response = await axios.post(
          `${config.apiUrl}/api/${url}`,
          tosend,
          {
            headers: { Authorization: `Bearer ${getCookie("_AT")}` },
          }
        );

        if (response.headers?.authorization) {
          if (`Bearer ${getCookie("_AT")}` !== response.headers.authorization) {
            setCookie("_AT", response.headers.authorization.split(" ")[1]);
          }
        }

        if (
          response.data &&
          !Array.isArray(response.data) &&
          response.data[param] !== null &&
          response.data[param] !== undefined &&
          typeof response.data[param] !== "object" &&
          !Array.isArray(response.data[param])
        ) {
          handleChange({
            target: {
              name: subname ? subname : name,
              value: response.data[param],
            },
          });
          setIsLoading(false); // Stop loading ✅
          return response.data[param];
        }
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false); // Make sure to stop loading even on error
    }
    setIsLoading(false); // Fallback stop
    return null;
  };

  const reloadClicked = async () => {
    // setIsLoading(true);
    // for testing loader purposes
    // const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    // await delay(2000); // Add a 2-second delay
    const s = await getLazyload(lazyload, lazyloadParam);
    setValue(s);
    // setIsLoading(false);
  };
  // console.log(isLoading);

  useEffect(() => {
    if (getFromValue === true) {
      if (valueLazyloaded.changed) {
        if (Array.isArray(lazyloadFieldChange) && currentFields) {
          let flag = false;
          let currentValues = valueLazyloaded.previousValue || {};
          for (const i in lazyloadFieldChange) {
            let f = lazyloadFieldChange[i];
            const findRelatedField = currentFields.find(
              (x) => x.name === f && x.changed === true
            );
            if (
              findRelatedField &&
              currentValues[f] !== findRelatedField?.defaultValue
            ) {
              //console.log('rtrt',name,f,currentValues[f],findRelatedField?.defaultValue)
              currentValues = {
                ...currentValues,
                [f]: findRelatedField.defaultValue,
              };
              flag = true;
            }
          }
          if (flag) {
            if (lazyload && lazyloadParam) {
              setValueLazyloaded({
                changed: false,
                previousValue: currentValues,
              });
              getLazyload(lazyload, lazyloadParam);
            }
          }
        }
      }
    }
  }, [valueLazyloaded]);

  useEffect(() => {
    if (label2 && currentFields && label2Param) {
      setSecondLabel(findData(currentFields, label2));
    }
    if (!label2Param) {
      setSecondLabel(label2);
    }

    setValueLazyloaded((prev) => {
      return { ...prev, changed: true };
    });
  }, [currentFields]);

  useEffect(() => {
    if (getFromValue === true) {
      if (lazyload && lazyloadParam && !defaultValue) {
        getLazyload(lazyload, lazyloadParam);
      } else {
        setValue(
          defaultValue
            ? defaultValue
            : decimalCount
            ? parseFloat(0).toFixed(decimalCount)
            : ""
        );
      }
      if (lazyloadFieldChange && currentFields) {
        const findRelatedField = currentFields.find(
          (x) => x.name === lazyloadFieldChange && x.changed === true
        );

        if (findRelatedField) {
          if (lazyload && lazyloadParam) {
            setValueLazyloaded((prev) => {
              return {
                changed: false,
                previousValue: findRelatedField.defaultValue,
              };
            });
            getLazyload(lazyload, lazyloadParam);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (getFromValue === true) {
      if (defaultValue) setValue(defaultValue);
    }
  }, [defaultValue]);

  const handleQuantityChange = (e) => {
    const qty = e.target.value;
    setQuantity(qty);

    if (!isNaN(qty)) {
      const total = Number(qty) * numberOfDays;
      setValue(total);
      handleChange({
        target: {
          name: quantityField != null ? quantityField : "rental_price_per_day",
          value: qty,
        },
      });
      handleChange({
        target: {
          name: subname ? subname : name,
          value: total,
        },
      });
    }
  };

  let isFieldDisabled = disabled || false;

  if (disabledIfField) {
    isFieldDisabled =
      isFieldDisabled ||
      disabledIfFieldChecker(currentFields, {
        field: disabledIfField.field,
        valuesArray: disabledIfField.isEqual,
      });
  }

  if (disabledIfField2) {
    isFieldDisabled =
      isFieldDisabled ||
      disabledIfFieldChecker(currentFields, {
        field: disabledIfField2.field,
        valuesArray: disabledIfField2.isEqual,
      });
  }

  const isEnabled = (rules) => {
    return rules.every((item) =>
      isFieldEnabledChecker(currentFields, {
        field: item.field,
        value: item.isEqual,
      })
    );
  };

  const isFieldDisabledEnd = alwaysEnabledIf
    ? !isEnabled(alwaysEnabledIf) && isFieldDisabled
    : isFieldDisabled;
  return (
    <div
      key={label + subname ? subname : name}
      className="flex items-center justify-between gap-x-6 py-5 px-4 border-b border-gray-200"
    >
      <div className="min-w-[30%]">
        <div className="flex items-start gap-x-3">
          {label && (
            <p className="text-sm font-semibold leading-6 text-gray-900">
              {label}
              {required ? "*" : ""}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-x-4">
        {secondLabel && (
          <span className="text-sm font-semibold leading-6 text-gray-900">
            {secondLabel}
          </span>
        )}
        <div className="flex items-center gap-2 relative">
          <input
            type="number"
            min="0"
            className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={quantity}
            onChange={handleQuantityChange}
            placeholder="Daily price"
            disabled={isFieldDisabledEnd || isLoading}
          />
          <span className="text-sm text-gray-500 flex items-center ">
            <span>×</span> <span>{numberOfDays}</span>
          </span>
          {lastRentalPricePerDay != null &&
            (quantity === "" || quantity == null) && (
              <div className="mt-1 text-[10px] text-gray-500 absolute bottom-[-40%]">
                Previous: {parseFloat(lastRentalPricePerDay).toFixed(2)}{" "}
                {secondLabel}
                /day
              </div>
            )}
        </div>

        {mainAction && mainAction?.name && (
          <a
            href={"#"}
            onClick={handleActionClick}
            name={mainAction?.id}
            title={mainAction?.name}
            data-id={id}
            className={`hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block`}
          >
            {mainAction?.name}
            <span className="sr-only">, {subname ? subname : name}</span>
          </a>
        )}
        {name && (
          <div className="relative">
            <div>
              <InputText
                name={subname ? subname : name}
                dataid={id}
                defaultValue={isLoading === true ? undefined : value}
                type={type ?? "text"}
                handleChange={inputChange}
                disabled={isFieldDisabledEnd || isLoading === true}
                decimalCount={decimalCount || null}
                minValue={minValue}
                maxValue={maxValue}
                classes="inline-block relative"
              />
              {isLoading === true && (
                <div
                  className="absolute"
                  style={{
                    top: "50%",
                    left: "5%",
                    transform: "translate(-0%,-50%)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="24"
                    height="24"
                    strokeWidth="2"
                  >
                    <path d="M12 3a9 9 0 1 0 9 9"></path>{" "}
                  </svg>
                </div>
              )}
            </div>

            {lazyload && lazyloadParam && isFieldDisabledEnd != true && (
              <ArrowPathIcon
                onClick={reloadClicked}
                className="w-5 absolute right-2 top-2 ml-1 cursor-pointer"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
